import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControl, InputLabel, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import * as XLSX from 'xlsx';
import iconv from 'iconv-lite';

const ExcelForm = ({ json1 = [], json2 = [], json3 = [] }) => {
  const [excelData, setExcelData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      setExcelData(jsonData);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(excelData.map((_, index) => index));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (index) => {
    if (selectedRows.includes(index)) {
      setSelectedRows(selectedRows.filter((i) => i !== index));
    } else {
      setSelectedRows([...selectedRows, index]);
    }
  };

  const exportExcel = () => {
    // Filter rows by selected indices
    const selectedData = excelData.filter((_, index) => selectedRows.includes(index));

    if (selectedData.length === 0) {
      alert('לא נבחרו רשומות לייצוא.');
      return;
    }

    const groupedData = groupDataByCemetery(selectedData); // Group only selected data

    Object.entries(groupedData).forEach(([cemeteryName, rows]) => {
      const updatedJson1 = updateJson1(json1Data, rows.length);
      const updatedJson2 = updateJson2(json2Data, cemeteryName, json3Data);

      const workbook = XLSX.utils.book_new();
      const sheetData = generateSheetData(updatedJson1, updatedJson2, rows);

      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, cemeteryName);

      XLSX.writeFile(workbook, `${cemeteryName}_report.xlsx`);
    });
  };

  const exportHK3_2 = () => {
    // Filter rows by selected indices
    const selectedData = excelData.filter((_, index) => selectedRows.includes(index));

    if (selectedData.length === 0) {
      alert('לא נבחרו רשומות לייצוא.');
      return;
    }

    const groupedData = groupDataByCemetery(selectedData); // Group only selected data

    Object.entries(groupedData).forEach(([cemeteryName, rows]) => {
      // Add JSON1 and JSON2 at the beginning
      const updatedJson1 = updateJson1(json1Data, rows.length);
      const updatedJson2 = updateJson2(json2Data, cemeteryName, json3Data);

      const header1 = generateRecordString(updatedJson1);
      const header2 = generateRecordString(updatedJson2);

      // Format rows
      const formattedRows = rows.map((row) => {
        return Object.values(row).join(''); // Concatenate fields without separators
      });

      // Add JSON3 at the end
      // const footer = generateRecordString(json3Data);
      const footer = `111${rows.length.toString().padStart(3, '0')}`;

      console.log('footer: ', footer);

      // Combine all parts
      const fullContent = [header1, header2, ...formattedRows, footer].join('\n');

      // Encode data to windows-1255 using iconv-lite
      const encodedData = iconv.encode(fullContent, 'windows-1255');

      const blob = new Blob([encodedData], { type: 'text/plain;charset=windows-1255' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${cemeteryName}_export.hk3`;
      link.click();
      window.URL.revokeObjectURL(url);
    });
  };
  const exportHK3 = () => {
    // Filter rows by selected indices
    const selectedData = excelData.filter((_, index) => selectedRows.includes(index));

    if (selectedData.length === 0) {
      alert('לא נבחרו רשומות לייצוא.');
      return;
    }

    const groupedData = groupDataByCemetery(selectedData); // Group only selected data

    Object.entries(groupedData).forEach(([cemeteryName, rows]) => {
      // Add JSON1 and JSON2 at the beginning
      const updatedJson1 = updateJson1(json1Data, rows.length);
      const updatedJson2 = updateJson2(json2Data, cemeteryName, json3Data);

      const sheetData = generateSheetData(updatedJson1, updatedJson2, rows).join('\n');

      const encodedData = iconv.encode(sheetData, 'windows-1255');

      const blob = new Blob([encodedData], { type: 'text/plain;charset=windows-1255' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${cemeteryName}_export.hk3`;
      link.click();
      window.URL.revokeObjectURL(url);
    });
  };

  const updateJson1 = (json, recordCount) => {
    if (!Array.isArray(json)) return []; // Return an empty array if json is not defined or not an array
    const date = new Date();
    const formattedDate = `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}`;

    return json.map((item) => {
      if (item.name === 'תאריך יצירת משלוח') {
        return { ...item, value: formattedDate };
      }
      if (item.name === 'סך רשומות במשלוח') {
        return { ...item, value: recordCount.toString().padStart(6, '0') };
      }
      // if (item.name === 'מספר משלוח') {
      //   return { ...item, value: recordCount.toString().padStart(3, '0') };
      // }
      return item;
    });
  };

  const updateJson2 = (json, cemeteryName, json3) => {
    if (!Array.isArray(json) || !Array.isArray(json3)) return []; // Validate both json arrays
    const cemeteryCode = json3.find((item) => item.name === cemeteryName)?.code || 'לא מזוהה';

    return json.map((item) => {
      if (item.name === 'קוד בית עלמין') {
        return { ...item, value: cemeteryCode };
      }
      return item;
    });
  };

  const groupDataByCemetery = (data) => {
    return data.reduce((acc, row) => {
      const cemeteryName = row['בית עלמין'] || 'לא מזוהה';
      if (!acc[cemeteryName]) acc[cemeteryName] = [];
      acc[cemeteryName].push(row);
      return acc;
    }, {});
  };

  const generateSheetData = (json1, json2, rows) => {
    const header1 = generateRecordString(json1);
    const header2 = generateRecordString(json2);

    const rowData = rows.map((row, index) => {
      console.log(row);

      return `222${(index + 1).toString().padStart(3, '0')}${generateRowData(row)}`;
    });

    const footer = `111${rows.length.toString().padStart(3, '0')}`;

    return [[header1], [header2], ...rowData.map((r) => [r]), [footer]];
  };

  const generateRecordString = (json) => {
    return json
      .map((item) => {
        let value = item.value || '';
        if (item.type === 'אלפנומרי') {
          value = value.split('').reverse().join('');
        }
        return value.padEnd(item.length, ' '); // Use padEnd to add spaces after the text
      })
      .join('');
  };

  const generateRowData = (row) => {
    const columns = [
      { field: 'נרכש בחיים', yes: 'כ', no: 'ל', empty: ' ' },
      { field: 'נגבה תשלום', yes: 'כ', no: 'ל', empty: ' ' },
      { field: 'מספר זהות', padLength: 9, padChar: '0', empty: ' '.repeat(9) },
      { field: 'סוג זהוי', mapping: { תז: 'ז', רשיון: 'ר', אלמוני: 'א', empty: ' ' } },
      { field: 'רשיון קבורה', padLength: 6, padChar: '0', empty: ' '.repeat(6) },
      { field: 'דרכון', padLength: 14, reverse: true, empty: ' '.repeat(14) },
      { field: 'שם משפחה', padLength: 17, reverse: false, empty: ' '.repeat(17) },
      { field: 'שם פרטי', padLength: 15, reverse: false, empty: ' '.repeat(15) },
      { field: 'תאריך לידה', format: 'YYYYMMDD', padLength: 8, empty: ' '.repeat(8) },
      { field: 'תאריך פטירה', format: 'YYYYMMDD', padLength: 8, empty: ' '.repeat(8) },
      { field: 'פטירה בחול', yes: 'כ', no: 'ל', empty: ' ' },
      { field: 'סוג זיהוי הורה', mapping: { תז: 'ז', דרכון: 'ד', empty: ' ' } },
      { field: 'מספר זהות הורה', padLength: 9, padChar: '0', empty: ' '.repeat(9) },
      { field: 'דרכון הורה', padLength: 14, reverse: true, empty: ' '.repeat(14) },
      { field: 'שם משפחה הורה', padLength: 17, reverse: true, empty: ' '.repeat(17) },
      { field: 'שם פרטי הורה', padLength: 15, reverse: true, empty: ' '.repeat(15) },
      { field: 'תאריך קבורה', format: 'YYYYMMDD', padLength: 8, empty: ' '.repeat(8) },
      { field: 'יום/לילה', yes: 'כ', no: 'ל', empty: ' ' },
      { field: 'שעת קבורה', padLength: 4, padChar: '0', empty: ' '.repeat(4) },
      { field: 'קוד גוש', padLength: 2, empty: ' '.repeat(2) },
      { field: 'קוד חלקה', padLength: 2, empty: ' '.repeat(2) },
      { field: 'שורה', padLength: 2, empty: ' '.repeat(2) },
      { field: 'קומה', padLength: 2, empty: ' '.repeat(2) },
      { field: 'מס קבר', padLength: 3, empty: ' '.repeat(3) },
      { field: 'מס תת קומה', padLength: 1, empty: ' '.repeat(1) }
    ];

    return columns
      .map(({ field, yes, no, empty, padLength, padChar, reverse, mapping, format }) => {
        let temp = 'מספר זהות';

        if ((row[field] || empty) === undefined) {
          console.log(1, 'field: ', field);
          console.log(2, 'yes: ', yes);
          console.log(3, 'no: ', no);
          console.log(4, 'padLength: ', padLength);
          console.log(5, 'padChar: ', padChar);
          console.log(6, 'reverse: ', reverse);
          console.log(7, 'mapping: ', mapping);
          console.log(8, 'format: ', format);
          console.log('row: ', row);
        }

        
        let value = row[field] || empty;
        
        if (typeof value === 'number') {
          value = value + ''
        }
        
        try {
          if (field === temp) {
            
            console.log('Checking condition: value !== undefined && value !== null');
            console.log('Type of value:', typeof value); 
            console.log('Value:', value, field);
          }
          
          // if (value !== undefined && value !== null) {
            // אם הערך קיים, נשתמש בו
            if (mapping) value = mapping[value] || mapping.empty;
            if (yes && no) value = value === 'כ' ? yes : value === 'ל' ? no : empty;
            if (reverse) value = value?.split('')?.reverse()?.join('');
            if (padLength && padChar) value = value?.padStart(padLength, padChar)?.slice(-padLength);
            if (padLength && !padChar && !format) value = value?.padEnd(padLength, ' ')
              // if (format && value) {
            //   const date = new Date(value);
            //   value = isNaN(date) ? empty : `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}`;
            // }
            // } else {
              if (field === temp) {
                console.log(1,'field: ',field);
                console.log(1,'value: ',value);
              }
              
            // // אם הערך חסר, נשלים אותו לפי ההגדרה
            // if (padLength && padChar) {
            //   value = ''.padStart(padLength, padChar); // השלמה עם אפסים
            // } else if (padLength) {
            //   value = ''.padEnd(padLength, ' '); // השלמה עם רווחים
            // } else {
            //   value = empty; // ברירת מחדל אם אין צורך בהשלמה
            // }
          // }
        } catch (error) {
          console.log(error);
          
        }



        if (field === temp) {
          console.log(10, 'value new: ', value);
        }

        return value || '';
      })
      .join('');
  };

  const json1Data = [
    { name: 'סוג רשומה', value: '@@@@', length: 4, type: 'אלפנומרי' },
    { name: 'קוד נושא', value: 'DK', length: 2, type: 'נומרי' },
    { name: 'סוג חומר', value: '001', length: 2, type: 'נומרי' },
    { name: 'סוג זיהוי שולח', value: '04', length: 2, type: 'נומרי' },
    { name: 'מספר תיק מעסיק', value: '94105897600', length: 11, type: 'נומרי' },
    { name: 'מספר משלוח', value: '051', length: 3, type: 'נומרי' },
    { name: 'בלנקים', value: '       ', length: 7, type: 'אלפנומרי' },
    { name: 'תאריך יצירת משלוח', value: '', length: 8, type: 'YYYYMMDD' },
    { name: 'אורך רשומה', value: '0176', length: 4, type: 'נומרי' },
    { name: 'סוג שולח', value: 'חק', length: 2, type: 'אלפנומרי' },
    { name: 'שם הגוף השולח', value: 'קדישא', length: 25, type: 'אלפנומרי' },
    { name: 'רחוב ומס בית', value: 'ת.ד 633', length: 25, type: 'אלפנומרי' },
    { name: 'ישוב', value: 'ירושלים', length: 10, type: 'אלפנומרי' },
    { name: 'מיקוד', value: '12345', length: 5, type: 'נומרי' },
    { name: 'איש קשר שם פרטי ומשפחה', value: 'ישראל ישראלי', length: 25, type: 'אלפנומרי' },
    { name: 'טלפון', value: '025472930', length: 9, type: 'נומרי' },
    { name: 'פקס', value: '025664460', length: 9, type: 'נומרי' },
    { name: 'סך רשומות במשלוח', value: '', length: 6, type: 'נומרי' }
  ];

  const json2Data = [
    { name: 'סוג רשומה', value: '111', length: 3, type: 'נומרי' },
    { name: 'קוד סניף ביטוח לאומי', value: '4100', length: 4, type: 'נומרי' },
    { name: 'קוד חברה', value: '006', length: 3, type: 'נומרי' },
    { name: 'קוד בית עלמין', value: '', length: 2, type: 'נומרי' },
    { name: 'מספר משלוח', value: '530', length: 3, type: 'נומרי' },
    { name: 'סוג תביעה', value: 'ח', length: 1, type: 'אלפנומרי' },
    { name: 'חדש(ח)/שינוי(ש)', value: 'ח', length: 1, type: 'אלפנומרי' }
  ];

  const json3Data = [
    { name: 'הר המנוחות גבעת שאול', code: '01' },
    { name: 'הר המנוחות גבעת שאול דרום', code: '03' },
    { name: 'הר הזיתים', code: '02' }
  ];

  return (
    <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: '8px' }}>
      <Typography variant="h5">טופס העלאת קובץ אקסל</Typography>
      <FormControl fullWidth margin="normal">
        <InputLabel htmlFor="excel-upload">בחר קובץ אקסל</InputLabel>
        <input id="excel-upload" type="file" accept=".xlsx, .xls" onChange={handleFileUpload} />
      </FormControl>

      {excelData.length > 0 && (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox onChange={handleSelectAll} checked={selectedRows.length === excelData.length} />
                  </TableCell>
                  {Object.keys(excelData[0]).map((key) => (
                    <TableCell key={key}>{key}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {excelData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedRows.includes(index)} onChange={() => handleRowSelect(index)} />
                    </TableCell>
                    {Object.values(row).map((value, i) => (
                      <TableCell key={i}>{value}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={2}>
            <Button variant="contained" color="primary" onClick={exportExcel} disabled={selectedRows.length === 0}>
              יצוא קובץ ביטוח לאומי
            </Button>
            <Button variant="contained" color="secondary" onClick={exportHK3} disabled={selectedRows.length === 0} sx={{ ml: 2 }}>
              יצוא קובץ .hk3
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ExcelForm;
